/**
 * FieldDetailsTab
 * --------------------------------------------------------
 * Content of the Farm View "Field details" tab — the Field View moved into
 * the trailing panel:
 *   header (field name) → details strip → Graphs → [Recommendation +
 *   Reported | Map].
 *
 * Layout follows the width of the tab itself, not the viewport (CSS
 * container queries in FieldDetailsTab.css): Recommendation/Reported sit
 * beside the map above 900px and stack above it below that; <=520px is the
 * Figma "small" frame.
 */
const React = require('react');
const PropTypes = require('prop-types');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const fieldManager = require('fieldManager');
const ExtendedFieldDetails = require('ExtendedFieldDetails');
const NextActionsContainer = require('NextActionsContainer');
const WaterMeterGraphContainer = require('WaterMeterGraphContainer');
const SoilTemperatureGraphContainer = require('SoilTemperatureGraphContainer');
const MapContainer = require('MapContainer');
const FieldDetailsWPGraph = require('./FieldDetailsWPGraph.jsx');
const RecommendationTileWrapper = require('../../planning/IrrigationRecommendation/RecommendationTileWrapper');
const {
  isSupportingPlannerByWeeks,
  shouldDisplaySoilTemperatureGraph,
} = require('@managers/fieldManager');
const {
  get_configuration_config,
  FeatureFlagDictionary,
} = require('remoteConfigurationsManager');
const {
  graphSectionEvent,
  buildGraphToggleIcons,
} = require('../graphSectionHelpers');
require('./FieldDetailsTab.css');

const { useState, useMemo } = React;

// Embedded map: 650px block = 16px title + 8px gap + 626px framed map (the
// frame's 1px borders leave 624px for Leaflet).
const MAP_HEIGHT = '624';

const useGraphToggleIcons = (field) => {
  const configurationData = useSelector((state) => state.configuration_data);
  const { farming_unit: farmingUnit, distribution } = useSelector(
    (state) => state.distribution_data.selected_entities,
  );
  const temperatureFlag = get_configuration_config(
    configurationData,
    FeatureFlagDictionary.TEMPERATURE_GRAPH,
    farmingUnit?.name,
    distribution?.name,
  );
  return buildGraphToggleIcons({
    showAccumulated: fieldManager.shouldDisplayAccumulatedIrrigation(field),
    temperatureFlag,
    hasSoilTemperatureData: shouldDisplaySoilTemperatureGraph(field),
  });
};

// The graph section the user picked, reset to the field's default graph when
// the field changes. Derived during render (not reset in an effect) so a
// field switch never renders the previous field's graph first.
const useGraphSection = (field) => {
  const [picked, setPicked] = useState(null);
  const defaultSection = useMemo(
    () => fieldManager.dashboardGraphToDisplay(field),
    [field],
  );
  const section =
    picked?.fieldId === field.id ? picked.section : defaultSection;

  // Tracking stays out of any state updater: updaters must be pure (React may
  // call them twice), which would double-count the event.
  const selectSection = (next) => {
    if (next === section) return;
    if (typeof mixpanel !== 'undefined' && mixpanel) {
      mixpanel.track?.(graphSectionEvent(next));
    }
    setPicked({ fieldId: field.id, section: next });
  };

  return [section, selectSection];
};

const FieldGraph = ({ section, toggleIcons, onToggleClick }) => {
  const temperatureUnit = useSelector(
    (state) => state.authentication_data?.user_data?.temperature || 'celsius',
  );
  const shared = { onToggleClick, temperatureUnit, graphsLayout: true };

  if (section === 'accumulated') {
    return <WaterMeterGraphContainer {...shared} icon={toggleIcons} />;
  }
  if (section === 'temperature') {
    return <SoilTemperatureGraphContainer {...shared} icon={toggleIcons} />;
  }
  return <FieldDetailsWPGraph {...shared} toggleIcons={toggleIcons} />;
};

FieldGraph.propTypes = {
  section: PropTypes.string.isRequired,
  toggleIcons: PropTypes.array.isRequired,
  onToggleClick: PropTypes.func.isRequired,
};

const FieldEvents = ({ field }) => (
  <div className='field-details-tab__events'>
    {isSupportingPlannerByWeeks(field) && (
      <RecommendationTileWrapper key={field.id} />
    )}
    <div className='field-details-tab__reported'>
      <NextActionsContainer
        selectedField={field}
        timezone={field?.geo_data?.time_zone}
        heigtAuto={true}
        farmLayout
        fieldHasPlan={fieldManager.isSupportingPlannerByDays(field)}
      />
    </div>
  </div>
);

FieldEvents.propTypes = { field: PropTypes.object.isRequired };

const FieldMap = ({ isArchive, hidden, onFieldSelect }) => {
  const { t } = useTranslation();
  return (
    <div className='field-details-tab__map'>
      <div className='field-details-tab__map-title'>{t('map')}</div>
      <div className='field-details-tab__map-frame'>
        <MapContainer
          embedded
          hidden={hidden}
          mapHeight={MAP_HEIGHT}
          archiveMode={isArchive}
          onFieldSelect={onFieldSelect}
        />
      </div>
    </div>
  );
};

FieldMap.propTypes = {
  isArchive: PropTypes.bool,
  hidden: PropTypes.bool,
  onFieldSelect: PropTypes.func,
};

const FieldDetailsTab = ({ field, isActive, onFieldSelect }) => {
  const fieldState = useSelector(
    (state) => state.distribution_data.field_state,
  );
  const toggleIcons = useGraphToggleIcons(field);
  const [section, selectSection] = useGraphSection(field);

  // The map is created the first time the tab is shown and then kept mounted
  // (hidden while the Farm map tab is showing), so switching tabs doesn't
  // rebuild Leaflet each time.
  const [mapMounted, setMapMounted] = useState(isActive);
  if (isActive && !mapMounted) setMapMounted(true);

  return (
    <div className='field-details-tab'>
      <div className='field-details-tab__header'>
        <span className='field-details-tab__field-name' title={field.name}>
          {field.name}
        </span>
      </div>
      <div className='field-details-tab__body'>
        <ExtendedFieldDetails variant='farm' />

        {/* The graph stays mounted while the tab is hidden so sensor
            samples start loading the moment a field is picked. */}
        <div className='field-details-tab__graphs'>
          <FieldGraph
            section={section}
            toggleIcons={toggleIcons}
            onToggleClick={selectSection}
          />
        </div>

        {mapMounted && (
          <div className='field-details-tab__wrap'>
            {isActive && fieldState === 'active' && (
              <FieldEvents field={field} />
            )}
            <FieldMap
              isArchive={fieldState === 'archive'}
              hidden={!isActive}
              onFieldSelect={onFieldSelect}
            />
          </div>
        )}
      </div>
    </div>
  );
};

FieldDetailsTab.propTypes = {
  field: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  onFieldSelect: PropTypes.func,
};

module.exports = FieldDetailsTab;
